import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaFileAlt, FaCloudUploadAlt, FaEye, FaTrash, FaCheck, FaDownload, FaCalendarAlt, FaFilePdf, FaInfoCircle, FaLink, FaCopy, FaExternalLinkAlt, FaSyncAlt } from 'react-icons/fa';
import Swal from 'sweetalert2';
import usePreviredPeriodo from '../../hooks/usePreviredPeriodo';
import * as previredService from '../../services/previredService';
import TrabajadorPrevired from '../../components/RRHH/TrabajadorPrevired';
import '../../styles/pages/RRHH/PreviredPage.css';

const MESES = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

const formatCLP = (val) => `$${parseInt(val || 0).toLocaleString('es-CL')}`;
const formatFecha = (val) => (val ? new Date(val).toLocaleDateString('es-CL') : '—');

const PreviredPage = ({ token, id_company }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const currentDate = new Date();
    // Al volver desde Ver detalle se llega con el período que se estaba mirando.
    const periodoInicial = location.state?.periodo || { mes: currentDate.getMonth() + 1, anio: currentDate.getFullYear() };
    const {
        mes, setMes, anio, setAnio,
        estado, loading, error,
        descargarTxt, descargando,
        subirPdf, subiendo,
        eliminarPdf,
    } = usePreviredPeriodo(id_company, periodoInicial.mes, periodoInicial.anio);

    const [pdfFile, setPdfFile] = useState(null);
    // Diferencias de un certificado que no cuadró. No se guardan: solo viven en pantalla.
    const [rechazo, setRechazo] = useState(null);

    useEffect(() => {
        setRechazo(null);
    }, [mes, anio]);

    // Estado link de pago Previred
    const [pagoPrevired, setPagoPrevired] = useState(null);
    const [generandoLink, setGenerandoLink] = useState(false);
    const [confirmandoPago, setConfirmandoPago] = useState(false);
    const [recalculandoPago, setRecalculandoPago] = useState(false);
    const [eliminandoLink, setEliminandoLink] = useState(false);

    const fetchPagoPrevired = useCallback(async () => {
        try {
            const data = await previredService.getEstadoPagoPrevired(id_company, mes, anio);
            setPagoPrevired(data.existe ? data : null);
        } catch {
            setPagoPrevired(null);
        }
    }, [id_company, mes, anio]);

    useEffect(() => {
        fetchPagoPrevired();
    }, [fetchPagoPrevired]);

    const pdf = estado?.pdf_cargado;

    // --- Handlers ---

    const handleDescargarTxt = async () => {
        try {
            Swal.fire({ title: 'Generando...', html: 'Preparando archivo TXT', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
            await descargarTxt();
            Swal.fire('Descargado', `Archivo previred_${String(mes).padStart(2, '0')}_${anio}.txt generado`, 'success');
        } catch (err) {
            Swal.fire('Error', err.message || 'Error al generar archivo', 'error');
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file && file.type === 'application/pdf') {
            setPdfFile(file);
        } else if (file) {
            Swal.fire('Error', 'Solo se permiten archivos PDF', 'error');
            e.target.value = '';
        }
    };

    const handleSubirPdf = async () => {
        if (!pdfFile) return;
        setRechazo(null);
        try {
            Swal.fire({ title: 'Procesando certificado...', html: 'Extrayendo datos con IA y comparando con las liquidaciones', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
            const { data } = await subirPdf(pdfFile);
            Swal.fire({
                title: 'Certificado contabilizado',
                html: `
                    <p><strong>Fecha de pago:</strong> ${formatFecha(data.fecha_pago)}</p>
                    <p><strong>Liquidaciones pagadas:</strong> ${data.liquidaciones_pagadas}</p>
                    <p><strong>Líneas de asiento generadas:</strong> ${data.asientos.lineas}</p>
                    <p><strong>Pagado a Previred:</strong> ${formatCLP(data.asientos.totalPrevired)}</p>
                `,
                icon: 'success'
            });
            setPdfFile(null);
            const input = document.getElementById('pdf-input');
            if (input) input.value = '';
        } catch (err) {
            const respuesta = err.response?.data;
            const comparacion = respuesta?.data?.comparacion;
            if (comparacion) {
                // No cuadró: nada se guardó, así que las diferencias se muestran acá.
                setRechazo({ mensaje: respuesta.message, colaboradores: comparacion.colaboradores });
                Swal.fire('No se contabilizó', 'Revise las diferencias debajo de las tarjetas.', 'error');
            } else {
                Swal.fire('No se contabilizó', respuesta?.message || 'Error al procesar el PDF', 'error');
            }
        }
    };

    const handleEliminar = async (id) => {
        const result = await Swal.fire({
            title: '¿Eliminar la carga de Previred?',
            html: `Se borrarán los asientos contables de <strong>${MESES[mes - 1]} ${anio}</strong> y sus liquidaciones volverán a PROPUESTA.<br><br>Después podrá subir el certificado de nuevo.`,
            icon: 'warning', showCancelButton: true, confirmButtonText: 'Sí, eliminar', cancelButtonText: 'Cancelar',
            confirmButtonColor: '#d33'
        });
        if (result.isConfirmed) {
            try {
                const respuesta = await eliminarPdf(id);
                Swal.fire('Carga eliminada', respuesta.message, 'success');
            } catch (err) {
                Swal.fire('Error', err.response?.data?.message || 'No se pudo eliminar la carga', 'error');
            }
        }
    };

    const handleGenerarLinkPago = async () => {
        setGenerandoLink(true);
        try {
            Swal.fire({ title: 'Calculando monto...', html: 'Sumando cotizaciones del período', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
            const data = await previredService.generarLinkPagoPrevired(id_company, mes, anio);
            Swal.close();
            setPagoPrevired({ existe: true, ...data });
            window.open(data.link_pago, '_blank');
        } catch (err) {
            Swal.fire('Error', err.response?.data?.error || err.message || 'Error al generar link de pago', 'error');
        } finally {
            setGenerandoLink(false);
        }
    };

    const handleCopiarLinkPago = () => {
        if (pagoPrevired?.link_pago) {
            navigator.clipboard.writeText(pagoPrevired.link_pago);
            Swal.fire({ icon: 'success', title: 'Link copiado', timer: 1200, showConfirmButton: false });
        }
    };

    const handleRecalcularPagoPrevired = async () => {
        const { isConfirmed } = await Swal.fire({
            title: 'Recalcular monto',
            text: `Se reemplazará el monto del link de ${MESES[mes - 1]} ${anio} con las liquidaciones actuales. Si el período ya no tiene liquidaciones, el link se eliminará.`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Recalcular',
            cancelButtonText: 'Cancelar'
        });
        if (!isConfirmed) return;

        setRecalculandoPago(true);
        try {
            Swal.fire({ title: 'Recalculando...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
            const data = await previredService.recalcularPagoPrevired(id_company, mes, anio);
            if (data.eliminado) {
                Swal.fire('Link eliminado', `El período no tiene liquidaciones. Se eliminó el link de ${formatCLP(data.monto_anterior)}.`, 'info');
            } else {
                Swal.fire('Recalculado', `Monto: ${formatCLP(data.monto_anterior)} → ${formatCLP(data.monto)}`, 'success');
            }
            await fetchPagoPrevired();
        } catch (err) {
            Swal.fire('Error', err.response?.data?.error || 'Error al recalcular pago', 'error');
        } finally {
            setRecalculandoPago(false);
        }
    };

    const handleEliminarLinkPago = async () => {
        if (!pagoPrevired?.codigo_pago) return;

        // Si el cliente ya pagó, se advierte qué se pierde: su comprobante y la confirmación.
        const yaPagado = pagoPrevired.estado !== 'pendiente';
        const periodo = `${MESES[mes - 1]} ${anio}`;
        const { isConfirmed } = await Swal.fire({
            title: '¿Eliminar el link de pago?',
            html: yaPagado
                ? `El link de <strong>${periodo}</strong> ${pagoPrevired.estado === 'confirmado' ? 'está <strong>confirmado</strong>' : 'tiene el <strong>comprobante del cliente</strong>'}.<br><br>Se eliminarán el link${pagoPrevired.comprobante_url ? ', el comprobante' : ''} y el registro del pago. Esta acción no se puede deshacer.`
                : `El link que tenga el cliente para <strong>${periodo}</strong> dejará de funcionar.<br><br>Después podrá generarlo de nuevo.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#d33'
        });
        if (!isConfirmed) return;

        setEliminandoLink(true);
        try {
            const data = await previredService.eliminarLinkPagoPrevired(pagoPrevired.codigo_pago);
            Swal.fire('Link eliminado', data.mensaje, 'success');
            await fetchPagoPrevired();
        } catch (err) {
            Swal.fire('Error', err.response?.data?.error || 'Error al eliminar el link de pago', 'error');
        } finally {
            setEliminandoLink(false);
        }
    };

    const handleConfirmarPagoPrevired = async () => {
        if (!pagoPrevired?.codigo_pago) return;
        setConfirmandoPago(true);
        try {
            Swal.fire({ title: 'Confirmando...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
            await previredService.confirmarPagoPrevired(
                pagoPrevired.codigo_pago,
                `Pago Previred confirmado manualmente para ${MESES[mes - 1]} ${anio}`
            );
            Swal.fire('Confirmado', 'Pago Previred confirmado exitosamente', 'success');
            await fetchPagoPrevired();
        } catch (err) {
            Swal.fire('Error', err.response?.data?.error || 'Error al confirmar pago', 'error');
        } finally {
            setConfirmandoPago(false);
        }
    };

    // --- Render ---

    return (
        <div className="previred-container">
            {/* Header */}
            <div className="previred-header">
                <div>
                    <h1><FaFileAlt /> Previred</h1>
                    <p>Declaraciones mensuales de cotizaciones previsionales</p>
                </div>
            </div>

            {/* Paso 1: Selector de periodo */}
            <div className="periodo-selector">
                <div className="periodo-selector-label">
                    <FaCalendarAlt />
                    <span>Seleccionar periodo</span>
                </div>
                <div className="periodo-selector-controls">
                    <select value={mes} onChange={(e) => setMes(parseInt(e.target.value))}>
                        {MESES.map((m, i) => (
                            <option key={i} value={i + 1}>{m}</option>
                        ))}
                    </select>
                    <select value={anio} onChange={(e) => setAnio(parseInt(e.target.value))}>
                        {[2024, 2025, 2026].map(y => (
                            <option key={y} value={y}>{y}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Estado de carga */}
            {loading && <div className="loading">Cargando estado del periodo...</div>}
            {error && <div className="error-msg"><FaInfoCircle /> {error}</div>}

            {/* Panel de acciones del periodo */}
            {!loading && estado && (
                <div className="periodo-panel">
                    {/* Tarjeta: Descargar TXT */}
                    <div className="action-card">
                        <div className="action-card-header">
                            <FaDownload />
                            <h3>Descargar TXT Previred</h3>
                        </div>
                        <div className="action-card-body">
                            {estado.tiene_liquidaciones ? (
                                <>
                                    <p className="action-info">{estado.cantidad_liquidaciones} liquidaciones encontradas para {MESES[mes - 1]} {anio}</p>
                                    <button className="btn-action btn-icon-tooltip btn-descargar" onClick={handleDescargarTxt} disabled={descargando} title={descargando ? 'Generando...' : 'Descargar TXT'}>
                                        <FaDownload /><span className="btn-tooltip-text">{descargando ? 'Generando...' : 'Descargar TXT'}</span>
                                    </button>
                                </>
                            ) : (
                                <p className="action-empty">No hay liquidaciones para este periodo. Debe crear las liquidaciones primero.</p>
                            )}
                        </div>
                    </div>

                    {/* Tarjeta: Link de Pago */}
                    <div className="action-card">
                        <div className="action-card-header">
                            <FaLink />
                            <h3>Link de Pago</h3>
                        </div>
                        <div className="action-card-body">
                            {!pagoPrevired ? (
                                <>
                                    <p className="action-info">Genera el link de pago para que el cliente conozca el monto y adjunte el comprobante de transferencia.</p>
                                    <button
                                        className="btn-action btn-icon-tooltip btn-descargar"
                                        onClick={handleGenerarLinkPago}
                                        disabled={generandoLink || !estado?.tiene_liquidaciones}
                                        title={!estado?.tiene_liquidaciones ? 'Debe tener liquidaciones generadas' : 'Generar Link de Pago'}
                                    >
                                        <FaLink /><span className="btn-tooltip-text">Generar Link</span>
                                    </button>
                                    {!estado?.tiene_liquidaciones && (
                                        <p className="action-empty">Debe generar las liquidaciones del período primero.</p>
                                    )}
                                </>
                            ) : (
                                <div className="pdf-cargado">
                                    <div className="pdf-cargado-info">
                                        <span className={`badge badge-${pagoPrevired.estado?.toLowerCase()}`}>{pagoPrevired.estado}</span>
                                        <p><strong>Monto:</strong> ${parseInt(pagoPrevired.monto || 0).toLocaleString('es-CL')}</p>
                                        {pagoPrevired.comprobante_url && (
                                            <p>
                                                <a href={pagoPrevired.comprobante_url} target="_blank" rel="noopener noreferrer" className="btn-link">
                                                    📄 Ver comprobante
                                                </a>
                                            </p>
                                        )}
                                    </div>
                                    <div className="pdf-cargado-actions">
                                        <button className="btn-action btn-icon-tooltip btn-ver-detalle" onClick={() => window.open(pagoPrevired.link_pago, '_blank')} title="Abrir Link">
                                            <FaExternalLinkAlt /><span className="btn-tooltip-text">Abrir Link</span>
                                        </button>
                                        <button className="btn-action btn-icon-tooltip btn-procesar" onClick={handleCopiarLinkPago} title="Copiar Link">
                                            <FaCopy /><span className="btn-tooltip-text">Copiar</span>
                                        </button>
                                        {pagoPrevired.estado === 'confirmado' ? (
                                            <span className="badge badge-procesado">✅ Pagado</span>
                                        ) : (
                                            <>
                                            <button
                                                className="btn-action btn-icon-tooltip btn-descargar"
                                                onClick={handleRecalcularPagoPrevired}
                                                disabled={recalculandoPago}
                                                title="Recalcular monto"
                                            >
                                                <FaSyncAlt /><span className="btn-tooltip-text">Recalcular</span>
                                            </button>
                                            <button
                                                className="btn-action btn-icon-tooltip btn-validar"
                                                onClick={handleConfirmarPagoPrevired}
                                                disabled={confirmandoPago}
                                                title="Confirmar Pago"
                                            >
                                                <FaCheck /><span className="btn-tooltip-text">Confirmar</span>
                                            </button>
                                            </>
                                        )}
                                        <button
                                            className="btn-action btn-icon-tooltip btn-eliminar"
                                            onClick={handleEliminarLinkPago}
                                            disabled={eliminandoLink}
                                            title="Eliminar Link"
                                        >
                                            <FaTrash /><span className="btn-tooltip-text">Eliminar</span>
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Tarjeta: Cargar PDF pagado */}
                    <div className="action-card">
                        <div className="action-card-header">
                            <FaFilePdf />
                            <h3>Certificado PDF Pagado</h3>
                        </div>
                        <div className="action-card-body">
                            {!pdf ? (
                                <div className="upload-zone">
                                    <p className="action-info">Suba el certificado Previred pagado. Si cuadra al peso con las liquidaciones, se generan los asientos contables y las liquidaciones quedan pagadas.</p>
                                    <div className="upload-form">
                                        <input
                                            type="file"
                                            id="pdf-input"
                                            accept=".pdf"
                                            onChange={handleFileChange}
                                            disabled={subiendo}
                                        />
                                        {pdfFile && <span className="file-name">{pdfFile.name}</span>}
                                        <button
                                            className="btn-action btn-icon-tooltip btn-upload"
                                            onClick={handleSubirPdf}
                                            disabled={!pdfFile || subiendo}
                                            title={subiendo ? 'Procesando...' : 'Subir y Extraer'}
                                        >
                                            <FaCloudUploadAlt /><span className="btn-tooltip-text">{subiendo ? 'Procesando...' : 'Subir y Extraer'}</span>
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="pdf-cargado">
                                    <div className="pdf-cargado-info">
                                        <span className="badge badge-procesado">Contabilizado</span>
                                        <p><strong>Archivo:</strong> {pdf.nombre_archivo}</p>
                                        <p><strong>Empleador:</strong> {pdf.empleador_razon_social}</p>
                                        <p><strong>Fecha de pago:</strong> {formatFecha(pdf.fecha_pago)}</p>
                                        <p><strong>Trabajadores:</strong> {pdf.total_trabajadores}</p>
                                        <p><strong>Total Rem.:</strong> {formatCLP(pdf.total_remuneraciones)}</p>
                                    </div>
                                    <div className="pdf-cargado-actions">
                                        <button className="btn-action btn-icon-tooltip btn-ver-detalle" onClick={() => navigate(`/rrhh/previred/detalle/${anio}/${mes}`)} title="Ver Detalle">
                                            <FaEye /><span className="btn-tooltip-text">Ver Detalle</span>
                                        </button>
                                        <button className="btn-action btn-icon-tooltip btn-eliminar" onClick={() => handleEliminar(pdf.id)} title="Eliminar">
                                            <FaTrash /><span className="btn-tooltip-text">Eliminar</span>
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Diferencias de un certificado que no cuadró */}
            {rechazo && (
                <div className="rechazo-panel">
                    <div className="rechazo-cabecera">
                        <h3>No se contabilizó el certificado de {MESES[mes - 1]} {anio}</h3>
                        <p>{rechazo.mensaje} No se guardó nada: corrija las liquidaciones y vuelva a subirlo.</p>
                    </div>
                    <div className="rechazo-lista">
                        {rechazo.colaboradores.map(trabajador => (
                            <TrabajadorPrevired key={trabajador.rut} trabajador={trabajador} abiertoInicial={!trabajador.cuadra} />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default PreviredPage;
